import { useState } from 'react';
import { galleryImages } from '../../../mocks/gallery';

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<typeof galleryImages[0] | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: "What types of wood do you use for your bowls?",
      answer: "We use a variety of locally sourced hardwoods including walnut, cherry, maple, and oak. Each piece of wood is carefully selected for its unique grain patterns and quality. We prioritize sustainable harvesting practices and often work with reclaimed wood when available."
    },
    {
      question: "How do I care for my wooden bowl?",
      answer: "Hand wash your bowl with mild soap and warm water, then dry immediately with a soft cloth. Never soak or put in the dishwasher. To maintain the finish, periodically apply food-safe mineral oil or beeswax. Avoid extreme temperatures and direct sunlight for prolonged periods."
    },
    {
      question: "Are your bowls food-safe?",
      answer: "Yes, all our bowls are finished with food-safe oils and waxes. We use only non-toxic finishes that are safe for serving food. Our finishing process ensures durability while maintaining the natural beauty of the wood."
    },
    {
      question: "Do you accept custom orders?",
      answer: "Absolutely! We love creating custom pieces tailored to your specific needs. Whether you have a particular wood type, size, or design in mind, we're happy to discuss your vision. Contact us to schedule a consultation and we'll work together to create your perfect piece."
    },
    {
      question: "How long does it take to complete an order?",
      answer: "Standard pieces typically ship within 2-3 weeks. Custom orders may take 4-6 weeks depending on complexity and wood availability. We'll provide a timeline estimate when you place your order and keep you updated throughout the process."
    },
    {
      question: "Do you offer shipping outside of Ohio?",
      answer: "Yes, we ship throughout the United States. Each bowl is carefully packaged to ensure safe delivery. Shipping costs are calculated based on destination and package size. Local pickup is also available by appointment at our workshop."
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Gallery
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore the beauty and craftsmanship of our handcrafted wooden bowls through our curated gallery
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((image) => (
            <div
              key={image.id}
              className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300"
              onClick={() => setSelectedImage(image)}
            >
              <div className="w-full h-80">
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{image.title}</h3>
                  <p className="text-sm text-white/90">{image.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl text-white"></i>
            </button>
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="w-full h-[70vh] rounded-lg overflow-hidden mb-6">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h3>
                <p className="text-lg text-white/80">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Frequently Asked Questions */}
        <div className="mt-24">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Frequently Asked Questions
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Find answers to common questions about our handcrafted wooden bowls
          </p>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-arrow-down-s-line text-2xl text-amber-600 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <p className="p-6 text-gray-700 leading-relaxed bg-white">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
